import { useCallback, useEffect, useRef, useState } from 'react';

import { useSearchParams } from '@savgroup-front-common/core/hooks';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import { OrderSummary } from '@savgroup-front-common/types';

import { IssueSummaryDto } from '../../../../types/IssueSummaryDto';
import useGetIssuesByOrder from '../../../app/hooks/useGetIssuesByOrder';
import { useSetClaimGroupIssueCommand } from '../../../app/hooks/useSetClaimGroupIssueCommand';
import { IRSH_QUERY_PARAMS } from '../IrshPages.constants';
import { IrshStepValues } from '../IrshPages.types';

interface UseIssuesPageArgs {
  orders: OrderSummary[] | undefined;
  onNextStep: RoutedStepProps<IrshStepValues>['onNextStep'];
}

const useIssuesPage = ({ orders, onNextStep }: UseIssuesPageArgs) => {
  const query = useSearchParams();
  const issueUniqueNameFromUrl = query.get(IRSH_QUERY_PARAMS.ISSUE);
  const reasonNameFromUrl = query.get(IRSH_QUERY_PARAMS.REASON);

  const { values, onValuesUpdate } =
    useRoutedStepsOrchestratorContext<IrshStepValues>();
  const issue = values?.issue;
  const persistedReasonNameFromUrl = values?.reasonNameFromUrl;
  const claimGroupId =
    values?.claimGroupId ??
    (values?.order?.products ?? [])
      .map((product) => product.claimGroupId)
      .find((id): id is string => Boolean(id));

  const [isSkipLoading, setIsSkipLoading] = useState(false);

  const orderIds = orders?.map((order) => order.orderId);
  const orderId = orderIds?.at(0) || undefined;

  const { issues, isIssuesLoading } = useGetIssuesByOrder({
    orderId,
  });

  const { handleSetClaimGroupIssueCommand } = useSetClaimGroupIssueCommand();

  const handleSelectIssue = useCallback(
    async (
      selectedIssue: IssueSummaryDto,
      { isAutoSelect = false }: { isAutoSelect?: boolean } = {},
    ) => {
      if (claimGroupId && selectedIssue.id) {
        const response = await handleSetClaimGroupIssueCommand({
          claimGroupId,
          issueId: selectedIssue.id,
        });

        if (!response) {
          return;
        }
      }

      onNextStep({
        newValues: { issue: selectedIssue },
        shouldReplace: isAutoSelect,
      });
    },
    [claimGroupId, handleSetClaimGroupIssueCommand, onNextStep],
  );

  const hasPersistedReasonNameRef = useRef(false);

  useEffect(() => {
    if (
      hasPersistedReasonNameRef.current ||
      !reasonNameFromUrl ||
      persistedReasonNameFromUrl
    ) {
      return;
    }

    hasPersistedReasonNameRef.current = true;

    onValuesUpdate({
      newValues: { reasonNameFromUrl: reasonNameFromUrl },
    });
  }, [reasonNameFromUrl, persistedReasonNameFromUrl, onValuesUpdate]);

  useEffect(() => {
    if (issue?.id || !issues?.length) {
      return;
    }

    const issueFromUrl = issueUniqueNameFromUrl
      ? issues.find((item) => item.uniqueName === issueUniqueNameFromUrl)
      : undefined;

    const issueToAutoSelect =
      issueFromUrl ?? (issues.length === 1 ? issues[0] : undefined);

    if (!issueToAutoSelect) {
      return;
    }

    setIsSkipLoading(true);

    handleSelectIssue(issueToAutoSelect, { isAutoSelect: true });
  }, [issues, issue?.id, issueUniqueNameFromUrl, handleSelectIssue]);

  return {
    issues: issues || [],
    isIssuesLoading,
    isSkipLoading,
    handleSelectIssue,
  };
};

export default useIssuesPage;
